import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Box, Grid2 } from '@mui/material';
import { Hub as HubIcon } from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import useSnackbar from '../hooks/useSnackbar';
import PageLayout from '../components/ui/PageLayout';
import LoadingState from '../components/ui/LoadingState';
import AppSnackbar from '../components/ui/AppSnackbar';
import ProductCatalog from '../components/billing/ProductCatalog';
import { useMeter } from '../hooks/useEntitlements';
import { pageContainer } from '../styles/pageStyles';

import WhatsAppHero from './integrations/WhatsAppHero';
import WhatsAppSettingsCard from './integrations/WhatsAppSettingsCard';
import WhatsAppPreviewAside from './integrations/WhatsAppPreviewAside';
import MessageHistoryCard from './integrations/MessageHistoryCard';

const DEFAULT_SETTINGS = {
  enabled: false,
  templateStyle: 2,
  messageNotes: '',
  notifyOnPublicCreate: true,
  notifyOnPrivateCreate: true,
  reminderEnabled: false,
  reminderHoursBefore: 24,
  reminderRespectBusinessHours: true,
  reminderBusinessStart: 8,
  reminderBusinessEnd: 18,
  ownerNotifyEnabled: false,
  ownerPhone: '',
  ownerNotifyOnCreate: true,
  ownerNotifyOnConfirm: true,
  ownerNotifyOnCancel: true,
};

// Exemplo mostrado no hero quando o catálogo de modelos ainda não carregou.
const FALLBACK_SAMPLE = `Oi, Maria! 😊 Deu tudo certo, o seu horário está confirmado!

✨ Serviço: Corte de cabelo
💈 Profissional: Ana
📍 Local: Salão X — Rua Y, 123
🗓️ Quando: 25/06/2026 às 14:30

Precisa mudar alguma coisa? É só acessar app.agendario.com.br/a/123.

Até logo! 💚`;

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { balance: waBalance, low: waLow } = useMeter('whatsapp_messages');
  const catalogRef = useRef(null);

  // ── Settings state ──────────────────────────────────────────────────────────
  const [form, setForm]                       = useState(DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings]   = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [models, setModels]                   = useState([]);

  // ── Messages state ──────────────────────────────────────────────────────────
  const [messages, setMessages]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary]     = useState(null);

  // ── Load settings on mount ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        setForm({ ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' });
        try { setModels(await whatsappService.getModels()); } catch { /* nomes/preview caem no fallback */ }
      } catch {
        // Silencioso — mantém defaults
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, []);

  // ── Load messages when page/rowsPerPage change ──────────────────────────────
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(page, rowsPerPage);
  }, [page, rowsPerPage, loadMessages]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Persiste as configurações atuais (com overrides opcionais).
  const persistSettings = useCallback(async (overrides = {}) => {
    const next = { ...form, ...overrides };
    await whatsappService.updateSettings({
      ...next,
      messageNotes: next.messageNotes?.trim() ? next.messageNotes.trim() : null,
      reminderHoursBefore: Math.min(168, Math.max(1, parseInt(next.reminderHoursBefore, 10) || 24)),
      reminderBusinessStart: clampHour(next.reminderBusinessStart, 8),
      reminderBusinessEnd: clampHour(next.reminderBusinessEnd, 18),
    });
  }, [form]);

  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await persistSettings();
      showSuccess('Configurações de WhatsApp salvas.');
    } catch {
      showError('Não foi possível salvar as configurações. Tente novamente.');
    } finally {
      setSavingSettings(false);
    }
  }, [persistSettings, showSuccess, showError]);

  // Liga/desliga a integração persistindo na hora (sem botão "Salvar" órfão).
  const handleToggleEnabled = useCallback(async (next) => {
    const prevEnabled = form.enabled;
    setForm((f) => ({ ...f, enabled: next }));
    setTogglingEnabled(true);
    try {
      await persistSettings({ enabled: next });
      showSuccess(next ? 'WhatsApp ativado.' : 'WhatsApp desativado.');
    } catch {
      setForm((f) => ({ ...f, enabled: prevEnabled })); // reverte
      showError('Não foi possível atualizar. Tente novamente.');
    } finally {
      setTogglingEnabled(false);
    }
  }, [form.enabled, persistSettings, showSuccess, showError]);

  // ── Pagination handlers ─────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleScrollToCatalog = () => {
    catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const selectedModel = models.find((m) => m.id === form.templateStyle);
  const sampleText = selectedModel?.previewCreated || FALLBACK_SAMPLE;

  return (
    <PageLayout
      title="Integrações"
      subtitle="Conecte o Agendário a serviços externos"
      icon={<HubIcon sx={{ color: 'primary.main' }} />}
    >
      <Box sx={pageContainer}>
        {loadingSettings ? (
          <LoadingState message="Carregando integrações..." />
        ) : !form.enabled ? (
          /* ── Estado DESATIVADO: hero de ativação + vitrine de pacotes ── */
          <>
            <WhatsAppHero
              sampleText={sampleText}
              onActivate={() => handleToggleEnabled(true)}
              activating={togglingEnabled}
            />
            <ProductCatalog meter="whatsapp_messages" />
          </>
        ) : (
          /* ── Estado ATIVADO: config + preview + pacotes + histórico ── */
          <>
            <Grid2 container spacing={3} alignItems="flex-start">
              <Grid2 size={{ xs: 12, lg: 8 }}>
                <WhatsAppSettingsCard
                  savingSettings={savingSettings}
                  togglingEnabled={togglingEnabled}
                  form={form}
                  updateField={updateField}
                  handleSave={handleSave}
                  onDisable={() => handleToggleEnabled(false)}
                  models={models}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, lg: 4 }}>
                <WhatsAppPreviewAside
                  selectedModel={selectedModel}
                  messageNotes={form.messageNotes}
                  waBalance={waBalance}
                  waLow={waLow}
                  onScrollToCatalog={handleScrollToCatalog}
                />
              </Grid2>
            </Grid2>

            {(waBalance === 0 || waLow) && (
              <Alert severity={waBalance === 0 ? 'error' : 'warning'} sx={{ mt: 3 }}>
                {waBalance === 0
                  ? 'Você está sem saldo de mensagens. Compre um pacote abaixo para voltar a enviar pelo WhatsApp.'
                  : `Seu saldo de mensagens está baixo (${waBalance}). Garanta um pacote abaixo para não ficar sem.`}
              </Alert>
            )}

            <Box ref={catalogRef} sx={{ mt: 3 }}>
              <ProductCatalog meter="whatsapp_messages" />
            </Box>

            <Box sx={{ mt: 3 }}>
              <MessageHistoryCard
                messages={messages}
                total={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                loading={loadingMessages}
                error={messagesError}
                setError={setMessagesError}
                summary={summary}
              />
            </Box>
          </>
        )}
      </Box>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
